/**
 * Browser Trailing SL Bot
 * Automatically tightens stop losses when profit thresholds are met
 * Matches Electron implementation with browser-specific adaptations
 * 
 * Features:
 * - Monitors positions for profit thresholds
 * - Moves SL to breakeven or trailing offset when threshold hit
 * - Per-asset settings and cooldowns
 * - Circuit breaker and error recovery
 * - Dry run mode for testing
 * - LocalStorage persistence (vs file system in Electron)
 */
class BrowserTrailingSLBot {
    constructor() {
        this.isEnabled = false;
        this.isInitialized = false;
        
        // Cross-tab coordination
        this.mode = 'inactive'; // 'leader', 'follower', 'inactive'
        
        // Settings (loaded from LocalStorage)
        this.settings = this.getDefaultSettings();
        
        // Per-asset state tracking
        this.assetStates = new Map(); // coin -> assetState
        
        // Rate limiting
        this.globalMoveCount = 0;
        this.globalMoveCountResetAt = Date.now() + 60000;
        
        // Global cycle throttling to prevent duplicate processing
        this.lastCycleTimestamp = 0;
        this.minCycleDurationMs = 3000; // Minimum 3 seconds between full cycles
        
        // Position data listener
        this.positionsDataListener = null;
        
        // console.log('[TrailingSLBot] 🎯 Initializing Browser Trailing SL Bot...');
        
        // Load settings and initialize
        this.loadSettings();
        this.initialize();
        
        // console.log(`[TrailingSLBot] Loaded state - Enabled: ${this.isEnabled}`);
    }

    /**
     * Get default settings
     */
    getDefaultSettings() {
        return {
            enabled: false,
            profitTriggerPct: 2.0,
            trailingMode: 'breakeven', // 'breakeven' | 'incremental'
            trailingOffsetPct: 0.2,
            minStepPct: 0.05,
            minDistanceFromPricePct: 0.5,
            cooldownMsPerAsset: 10000,
            maxMovesPerMinute: 30,
            decisionThrottleMs: 5000,
            positionStabilityMs: 10000,
            perAssetOverrides: {}
        };
    }

    /**
     * Initialize the bot
     */
    async initialize() {
        try {
            // Set up listener for positions-data events
            this.setupPositionsListener();
            
            this.isInitialized = true;
            this.log('TrailingSLBot initialized successfully');
            
            return true;
        } catch (error) {
            // console.error('[TrailingSLBot] Initialization error:', error);
            return false;
        }
    }

    /**
     * Set up listener for positions-data CustomEvent
     */
    setupPositionsListener() {
        // Remove old listener if exists
        if (this.positionsDataListener) {
            window.removeEventListener('positions-data', this.positionsDataListener);
        }
        
        // Create new listener
        this.positionsDataListener = (event) => {
            if (this.isEnabled && this.isInitialized && this.mode === 'leader') {
                const positionsData = event.detail;
                this.onPositionsUpdate(positionsData).catch(err => {
                    // console.error('[TrailingSLBot] Update error:', err);
                });
            }
        };
        
        // Add listener
        window.addEventListener('positions-data', this.positionsDataListener);
        this.log('Positions listener registered');
    }

    /**
     * Load settings from LocalStorage
     */
    loadSettings() {
        try {
            const saved = localStorage.getItem('trailingSLBotSettings');
            if (saved) {
                const loadedSettings = JSON.parse(saved);
                this.settings = { ...this.getDefaultSettings(), ...loadedSettings };
                this.isEnabled = this.settings.enabled;
                this.log('Settings loaded from LocalStorage');
            } else {
                // Save default settings
                this.saveSettings();
                this.log('Created default settings');
            }
        } catch (error) {
            // console.error('[TrailingSLBot] Error loading settings:', error);
            this.settings = this.getDefaultSettings();
        }
    }

    /**
     * Save settings to LocalStorage
     */
    saveSettings() {
        try {
            localStorage.setItem('trailingSLBotSettings', JSON.stringify(this.settings));
            this.log('Settings saved to LocalStorage');
        } catch (error) {
            // console.error('[TrailingSLBot] Error saving settings:', error);
        }
    }

    /**
     * Toggle bot enabled state
     */
    toggle() {
        this.isEnabled = !this.isEnabled;
        this.settings.enabled = this.isEnabled;
        this.saveSettings();
        this.log(`Bot ${this.isEnabled ? 'enabled' : 'disabled'}`);
        return this.isEnabled;
    }

    /**
     * Enable/disable bot
     */
    setEnabled(enabled) {
        this.isEnabled = enabled;
        this.settings.enabled = enabled;
        this.saveSettings();
    }

    /**
     * Update settings
     */
    async updateSettings(newSettings) {
        this.settings = { ...this.settings, ...newSettings };
        this.isEnabled = this.settings.enabled;
        await this.saveSettings();
        this.log('Settings updated');
    }

    /**
     * Get current status
     */
    getStatus() {
        const assetStatesArray = Array.from(this.assetStates.entries()).map(([coin, state]) => ({
            coin,
            lastSLPrice: state.lastSLPrice,
            lastActionTimestamp: state.lastActionTimestamp,
            moveCount: state.moveCount,
            pausedUntil: state.pausedUntil,
            consecutiveErrorCount: state.consecutiveErrorCount,
            recentErrors: state.recentErrors
        }));

        return {
            enabled: this.isEnabled,
            initialized: this.isInitialized,
            settings: this.settings,
            assetStates: assetStatesArray,
            globalMoveCount: this.globalMoveCount,
            timestamp: Date.now()
        };
    }

    /**
     * Main entry point - called when positions data updates
     */
    async onPositionsUpdate(positionsData) {
        if (!this.isEnabled || !this.isInitialized) {
            return;
        }

        try {
            // Global cycle throttling
            const now = Date.now();
            const timeSinceLastCycle = now - this.lastCycleTimestamp;
            if (timeSinceLastCycle < this.minCycleDurationMs) {
                return; // Skip - too soon since last evaluation
            }
            this.lastCycleTimestamp = now;

            // Reset global rate limit counter if needed
            if (Date.now() >= this.globalMoveCountResetAt) {
                this.globalMoveCount = 0;
                this.globalMoveCountResetAt = Date.now() + 60000;
            }

            // Check global rate limit
            if (this.globalMoveCount >= this.settings.maxMovesPerMinute) {
                return; // Skip due to rate limit
            }

            // Fetch market prices once for all positions
            if (!window.BrowserHyperliquidClient || !window.BrowserHyperliquidClient.isInitialized) {
                this.log('BrowserHyperliquidClient not available, skipping cycle');
                return;
            }

            // Try WebSocket first
            let allMids;
            if (window.BrowserPriceService && window.BrowserPriceService.isReady()) {
                allMids = window.BrowserPriceService.getAllPrices();
                if (!allMids || Object.keys(allMids).length === 0) {
                    allMids = null;
                }
            }
            
            // Fallback to API
            if (!allMids) {
                allMids = await window.BrowserHyperliquidClient.infoClient.allMids();
            }
            
            if (!allMids || Object.keys(allMids).length === 0) {
                this.log('Failed to fetch market prices, skipping cycle');
                return;
            }

            // Process each position
            const allPositions = positionsData.positions || [];
            const positionCount = allPositions.length;
            
            // Log all position coins for debugging
            const positionCoins = allPositions.map(p => `${p.coin}(size:${p.size})`).join(', ');
            this.log(`Evaluating ${positionCount} positions: [${positionCoins}]`);
            
            for (const position of allPositions) {
                try {
                    // Skip positions with zero or near-zero size
                    if (!position.size || Math.abs(position.size) < 0.0001) {
                        this.log(`[${position.coin}] Skipping - position size is zero or negligible (${position.size})`);
                        continue;
                    }
                    
                    await this.evaluatePosition(position, positionsData.orders || [], allMids);
                } catch (error) {
                    this.logError(position.coin, `Error evaluating position: ${error.message}`);
                }
            }
        } catch (error) {
            // console.error('[TrailingSLBot] Error in onPositionsUpdate:', error);
        }
    }

    /**
     * Evaluate a single position for trailing SL adjustment
     */
    async evaluatePosition(position, orders, allMids) {
        const asset = position.coin;
        const assetState = this.getOrCreateAssetState(asset);

        // Decision throttling
        const timeSinceLastDecision = Date.now() - (assetState.lastDecisionTimestamp || 0);
        if (timeSinceLastDecision < this.settings.decisionThrottleMs) {
            const remainingThrottle = Math.ceil((this.settings.decisionThrottleMs - timeSinceLastDecision) / 1000);
            this.log(`[${asset}] Throttled - ${remainingThrottle}s until next decision`);
            return;
        }
        assetState.lastDecisionTimestamp = Date.now();

        // Get current market price
        const currentPrice = parseFloat(allMids[asset]);
        if (!currentPrice || isNaN(currentPrice)) {
            this.logError(asset, `Market price not available (allMids[${asset}] = ${allMids[asset]})`);
            return;
        }

        // Find current SL order
        const currentSL = this.findCurrentSL(position, orders);
        
        // Log SL detection result
        if (currentSL) {
            this.log(`[${asset}] Found current SL: price=${currentSL.limitPx}, size=${currentSL.size}, oid=${currentSL.oid}`);
        } else {
            this.log(`[${asset}] No SL order detected for position (size=${position.size}, side=${position.side}, entry=${position.entryPrice})`);
        }
        
        // Calculate PnL percentage
        const pnlPct = this.calculatePnLPct(position, currentPrice);
        
        // Get effective settings for this asset
        const effectiveSettings = this.getEffectiveSettings(asset);
        
        // Check if profit threshold is met
        if (pnlPct < effectiveSettings.profitTriggerPct) {
            this.logDecision(asset, {
                position,
                currentPrice,
                pnlPct,
                currentSL: currentSL?.limitPx || null,
                action: 'skip',
                reason: `PnL ${pnlPct.toFixed(2)}% below threshold ${effectiveSettings.profitTriggerPct}%`
            });
            return;
        }

        // No SL exists
        if (!currentSL) {
            this.logDecision(asset, {
                position,
                currentPrice,
                pnlPct,
                currentSL: null,
                action: 'skip',
                reason: `No SL order found (PnL ${pnlPct.toFixed(2)}% above threshold, but can't modify without existing SL)`
            });
            return;
        }

        // Compute new SL price
        const newSL = this.computeNewSL(position, currentPrice, effectiveSettings);
        if (newSL === null) {
            this.logDecision(asset, {
                position,
                currentPrice,
                pnlPct,
                currentSL: currentSL.limitPx,
                action: 'skip',
                reason: 'Failed to compute new SL'
            });
            return;
        }

        // Safety checks
        if (!this.isMoreProtective(newSL, currentSL.limitPx, position.side === 'LONG')) {
            this.logDecision(asset, {
                position,
                currentPrice,
                pnlPct,
                currentSL: currentSL.limitPx,
                newSL,
                action: 'skip',
                reason: 'New SL is not more protective'
            });
            return;
        }

        if (!this.hasMinDistance(newSL, currentPrice, position.side === 'LONG', effectiveSettings.minDistanceFromPricePct)) {
            this.logDecision(asset, {
                position,
                currentPrice,
                pnlPct,
                currentSL: currentSL.limitPx,
                newSL,
                action: 'skip',
                reason: `New SL too close to current price (${effectiveSettings.minDistanceFromPricePct}%)`
            });
            return;
        }

        if (!this.isPositionStable(position, assetState.lastPositionSnapshot, effectiveSettings.positionStabilityMs)) {
            this.logDecision(asset, {
                position,
                currentPrice,
                pnlPct,
                currentSL: currentSL.limitPx,
                newSL,
                action: 'skip',
                reason: 'Position size recently changed, waiting for stability'
            });
            return;
        }

        // Check minimum step size
        const stepPct = Math.abs(newSL - currentSL.limitPx) / currentSL.limitPx * 100;
        if (stepPct < effectiveSettings.minStepPct) {
            this.logDecision(asset, {
                position,
                currentPrice,
                pnlPct,
                currentSL: currentSL.limitPx,
                newSL,
                action: 'skip',
                reason: `Step size ${stepPct.toFixed(2)}% below minimum ${effectiveSettings.minStepPct}%`
            });
            return;
        }

        // Check cooldown
        const timeSinceLastAction = Date.now() - (assetState.lastActionTimestamp || 0);
        if (timeSinceLastAction < effectiveSettings.cooldownMsPerAsset) {
            this.logDecision(asset, {
                position,
                currentPrice,
                pnlPct,
                currentSL: currentSL.limitPx,
                newSL,
                action: 'skip',
                reason: `Cooldown active (${Math.ceil((effectiveSettings.cooldownMsPerAsset - timeSinceLastAction) / 1000)}s remaining)`
            });
            return;
        }

        // All checks passed - move SL
        this.log(`[${asset}] ✅ All checks passed! Proceeding to move SL from ${currentSL.limitPx} to ${newSL}`);
        this.logDecision(asset, {
            position,
            currentPrice,
            pnlPct,
            currentSL: currentSL.limitPx,
            newSL,
            action: 'move',
            reason: 'All checks passed, moving SL'
        });

        // Execute live modification
        await this.moveSLForPosition(position, currentSL, newSL, currentPrice, pnlPct);

        // Update position snapshot
        assetState.lastPositionSnapshot = {
            size: position.size,
            entryPrice: position.entryPrice,
            timestamp: Date.now()
        };
    }

    /**
     * Find current SL order for a position
     */
    findCurrentSL(position, orders) {
        const asset = position.coin;
        
        // Log all orders for this asset
        const assetOrders = orders.filter(o => o.coin === asset);
        this.log(`[${asset}] Searching for SL among ${assetOrders.length} orders for this asset`);
        
        // Filter TP/SL orders for this position
        const tpslOrders = orders.filter(order => {
            const matches = order.coin === position.coin &&
                   order.reduceOnly === true &&
                   this.sizesMatch(position.size, order.size);
            
            if (order.coin === asset) {
                this.log(`[${asset}] Order oid=${order.oid}: reduceOnly=${order.reduceOnly}, orderSize=${order.size}, posSize=${position.size}, sizeMatch=${this.sizesMatch(position.size, order.size)}, limitPx=${order.limitPx}`);
            }
            
            return matches;
        });

        if (tpslOrders.length === 0) {
            this.log(`[${asset}] No TP/SL orders found (checked reduceOnly=true, size match)`);
            return null;
        }
        
        this.log(`[${asset}] Found ${tpslOrders.length} TP/SL orders, classifying as TP or SL...`);

        // Classify TP vs SL by price relative to entry
        const isLong = position.side === 'LONG';
        const slOrders = tpslOrders.filter(order => {
            const isProfitable = isLong ? 
                (order.limitPx > position.entryPrice) : 
                (order.limitPx < position.entryPrice);
            const isSL = !isProfitable;
            
            this.log(`[${asset}] Order oid=${order.oid} at ${order.limitPx} vs entry ${position.entryPrice}: ${isSL ? 'SL' : 'TP'} (${isLong ? 'LONG' : 'SHORT'})`);
            
            return isSL;
        });

        if (slOrders.length === 0) {
            this.log(`[${asset}] No SL orders found after classification (all orders are TP side)`);
            return null;
        }

        // Select most conservative SL if multiple exist
        const currentSL = isLong ?
            slOrders.reduce((a, b) => a.limitPx > b.limitPx ? a : b) :  // Highest for long
            slOrders.reduce((a, b) => a.limitPx < b.limitPx ? a : b);   // Lowest for short

        return currentSL;
    }

    /**
     * Calculate PnL percentage
     */
    calculatePnLPct(position, currentPrice) {
        const isLong = position.side === 'LONG';
        if (isLong) {
            return ((currentPrice - position.entryPrice) / position.entryPrice) * 100;
        } else {
            return ((position.entryPrice - currentPrice) / position.entryPrice) * 100;
        }
    }

    /**
     * Compute new SL price based on mode and settings
     */
    computeNewSL(position, currentPrice, settings) {
        const isLong = position.side === 'LONG';
        
        if (settings.trailingMode === 'breakeven') {
            // Move SL to entry price (breakeven)
            return position.entryPrice;
        } else if (settings.trailingMode === 'incremental') {
            // Move SL based on offset from entry
            const offset = position.entryPrice * (settings.trailingOffsetPct / 100);
            const newSL = isLong ? 
                position.entryPrice + offset :
                position.entryPrice - offset;
            return newSL;
        }
        
        return null;
    }

    /**
     * Move SL for a position (cancel old, place new)
     */
    async moveSLForPosition(position, currentSL, newSL, currentPrice, pnlPct) {
        const asset = position.coin;
        this.log(`[${asset}] 🔄 LIVE MODE: Starting SL modify operation...`);
        this.log(`[${asset}]   Current SL: ${currentSL.limitPx} (oid=${currentSL.oid})`);
        this.log(`[${asset}]   New SL: ${newSL}`);
        this.log(`[${asset}]   Position: ${position.side} ${Math.abs(position.size)} @ ${position.entryPrice}`);
        this.log(`[${asset}]   PnL: ${pnlPct.toFixed(2)}%, Current Price: ${currentPrice}`);
        
        try {
            // Use batchModify to change SL price
            this.log(`[${asset}] Modifying SL oid=${currentSL.oid} from ${currentSL.limitPx} to ${newSL}...`);
            
            const placeResult = await this.retryWithBackoff(async () => {
                // Re-fetch current orders to verify OID still exists
                const freshOrders = await window.BrowserHyperliquidClient.getUserOpenOrders();
                const stillExists = freshOrders.find(o => String(o.oid) === String(currentSL.oid));
                
                if (!stillExists) {
                    throw new Error(`Order ${currentSL.oid} no longer exists (filled/cancelled before modify)`);
                }
                
                // Get asset index and cache szDecimals
                const assetIndex = await window.BrowserHyperliquidClient.getAssetIndex(asset);
                
                if (assetIndex === -1) {
                    throw new Error(`Asset ${asset} not found in universe`);
                }
                
                // Get asset metadata (after getAssetIndex caches szDecimals)
                const meta = await window.BrowserHyperliquidClient.infoClient.meta();
                const assetMeta = meta.universe[assetIndex];
                this.log(`[${asset}] Asset meta: szDecimals=${assetMeta.szDecimals}`);
                
                // Round price and size using client's methods
                const roundedPrice = window.BrowserHyperliquidClient.roundToTickSize(newSL);
                const roundedSize = window.BrowserHyperliquidClient.roundToSizeDecimals(Math.abs(position.size));
                
                // Validate rounding succeeded
                if (roundedPrice === null || roundedSize === null) {
                    throw new Error(`Failed to round price/size: price=${newSL} → ${roundedPrice}, size=${position.size} → ${roundedSize}`);
                }
                
                const priceStr = String(roundedPrice);
                const sizeStr = String(roundedSize);
                
                this.log(`[${asset}] Rounded: ${newSL} → ${priceStr} (tick size), ${position.size} → ${sizeStr} (szDecimals=${assetMeta.szDecimals})`);
                this.log(`[${asset}] Using price=${priceStr} (entry=${position.entryPrice}, newSL=${newSL})`);
                
                // For SL: LONG positions need SELL to close (b=false), SHORT positions need BUY to close (b=true)
                const isBuy = position.side === 'SHORT';
                
                const modifies = [{
                    oid: parseInt(currentSL.oid),
                    order: {
                        a: assetIndex,
                        b: isBuy,
                        p: priceStr,
                        s: sizeStr,
                        r: true,
                        t: {
                            trigger: {
                                isMarket: true,
                                triggerPx: priceStr,
                                tpsl: 'sl'
                            }
                        }
                    }
                }];
                
                this.log(`[${asset}] Calling batchModify: oid=${currentSL.oid}, price=${priceStr}, size=${sizeStr}, side=${isBuy ? 'BUY' : 'SELL'}, position=${position.side}`);
                
                // Execute modify via BrowserHyperliquidClient
                const response = await window.BrowserHyperliquidClient.exchClient.batchModify({ modifies });
                
                this.log(`[${asset}] ✅ Successfully modified SL: ${JSON.stringify(response)}`);
                
                // Extract new OID from response
                let newOid = currentSL.oid;
                if (response && response.response && response.response.data && response.response.data.statuses) {
                    const statuses = response.response.data.statuses;
                    if (statuses[0] && statuses[0].resting) {
                        newOid = statuses[0].resting.oid;
                        this.log(`[${asset}] New SL OID: ${newOid}`);
                    }
                }
                
                return { success: true, oid: newOid, cloid: null };
            }, asset);
            
            // Update state
            const assetState = this.getOrCreateAssetState(asset);
            assetState.lastSLPrice = newSL;
            assetState.lastSLCloid = placeResult.cloid;
            assetState.lastSLOrderId = currentSL.oid;
            assetState.lastActionTimestamp = Date.now();
            assetState.moveCount++;
            this.globalMoveCount++;
            
            this.log(`[${asset}] 🎉 SL MOVE COMPLETE: ${currentSL.limitPx} → ${newSL}`);
            return { success: true };
            
        } catch (error) {
            this.log(`[${asset}] ❌ FAILED to move SL: ${error.message}`);
            this.logError(asset, `Move SL failed: ${error.message}`);
            return { success: false, error: error.message };
        }
    }

    /**
     * Retry operation with exponential backoff
     */
    async retryWithBackoff(operation, asset, maxRetries = 2) {
        for (let attempt = 0; attempt <= maxRetries; attempt++) {
            try {
                const result = await operation();
                // Reset error count on success
                const assetState = this.getOrCreateAssetState(asset);
                assetState.consecutiveErrorCount = 0;
                return result;
            } catch (error) {
                this.log(`[${asset}] ❌ Attempt ${attempt + 1}/${maxRetries + 1} failed: ${error.message}`);
                
                // If order doesn't exist, don't retry
                if (error.message && error.message.includes('no longer exists')) {
                    this.log(`[${asset}] Order was cancelled/filled - stopping retries`);
                    throw error;
                }
                
                if (attempt === maxRetries) {
                    const assetState = this.getOrCreateAssetState(asset);
                    assetState.consecutiveErrorCount++;
                    this.log(`[${asset}] ❌ ALL RETRIES FAILED. Error: ${error.message}`);
                    throw error;
                }
                const delayMs = Math.min(1000 * Math.pow(2, attempt), 5000);
                this.log(`[${asset}] Retrying in ${delayMs}ms...`);
                await new Promise(resolve => setTimeout(resolve, delayMs));
            }
        }
    }

    /**
     * Get or create asset state
     */
    getOrCreateAssetState(coin) {
        if (!this.assetStates.has(coin)) {
            this.assetStates.set(coin, {
                lastSLPrice: null,
                lastSLOrderId: null,
                lastSLCloid: null,
                lastActionTimestamp: null,
                lastDecisionTimestamp: null,
                moveCount: 0,
                pausedUntil: null,
                lastPositionSnapshot: null,
                recentErrors: [],
                consecutiveErrorCount: 0
            });
        }
        return this.assetStates.get(coin);
    }

    /**
     * Get effective settings for asset (with per-asset overrides)
     */
    getEffectiveSettings(asset) {
        const overrides = this.settings.perAssetOverrides[asset] || {};
        return { ...this.settings, ...overrides };
    }

    /**
     * Size matching with tolerance
     */
    sizesMatch(positionSize, orderSize, tolerance = 0.0001) {
        return Math.abs(Math.abs(positionSize) - Math.abs(orderSize)) < tolerance;
    }

    /**
     * Check if new SL is more protective
     */
    isMoreProtective(newSL, currentSL, isLong) {
        if (!currentSL) return true;
        return isLong ? (newSL >= currentSL) : (newSL <= currentSL);
    }

    /**
     * Check minimum distance from current price
     */
    hasMinDistance(newSL, currentPrice, isLong, minDistancePct) {
        const minDistance = currentPrice * (minDistancePct / 100);
        if (isLong) {
            return (currentPrice - newSL) >= minDistance;
        } else {
            return (newSL - currentPrice) >= minDistance;
        }
    }

    /**
     * Check position stability
     */
    isPositionStable(position, lastSnapshot, stabilityMs) {
        if (!lastSnapshot) return true;
        const timeDiff = Date.now() - lastSnapshot.timestamp;
        const sizeDiff = Math.abs(position.size - lastSnapshot.size);
        return timeDiff >= stabilityMs || sizeDiff < 0.0001;
    }

    /**
     * Log decision
     */
    logDecision(asset, data) {
        this.log(`[${asset}] ${data.action}: ${data.reason}`);
    }

    /**
     * Log error
     */
    logError(asset, message) {
        // console.error(`[TrailingSLBot] [${asset}] ERROR: ${message}`);
        
        const assetState = this.getOrCreateAssetState(asset);
        assetState.recentErrors.push({
            timestamp: Date.now(),
            message: message
        });
        
        // Keep only last 5 errors
        if (assetState.recentErrors.length > 5) {
            assetState.recentErrors.shift();
        }
    }

    /**
     * Log message
     */
    log(message) {
        // console.log(`[TrailingSLBot] ${message}`);
    }

    /**
     * Set mode (for TabCoordinator)
     * @param {string} newMode - 'leader' or 'follower'
     */
    async setMode(newMode) {
        console.log(`[TrailingSLBot] Switching mode: ${this.mode} -> ${newMode}`);
        this.mode = newMode;
    }
}

// Create and expose singleton instance
if (typeof window !== 'undefined') {
    window.BrowserTrailingSLBot = new BrowserTrailingSLBot();
    // console.log('✅ Browser Trailing SL Bot service initialized');
}

// Export for ES6 modules
export { BrowserTrailingSLBot };
